import React, { useEffect, useMemo, useState } from 'react';
import MapCanvas from './MapCanvas';
import { CITY_TYPES } from '../../pages/city/cityUtils';

const MapTestModal = ({
  isOpen,
  onClose,
  sector,
  tiles = [],
  portals = [],
  enemies = [],
  objects = [],
  cities = [],
  missionEvents = [],
  tilesetConfig = null
}) => {
  const width = sector?.width || 20;
  const height = sector?.height || 15;

  const blockedTerrainIds = useMemo(() => new Set([2, 3, 4, 5, 8, 11, 13, 14]), []);
  const terrainById = useMemo(() => {
    const map = new Map();
    (tilesetConfig?.terrains || []).forEach((terrain) => {
      map.set(Number(terrain.terrainId), terrain);
    });
    return map;
  }, [tilesetConfig]);

  const firstWalkableCoordinate = useMemo(() => {
    const terrainTiles = tiles.filter(t => Number(t.layer) === 0);
    const candidate = terrainTiles.find((t) => {
      const terrain = terrainById.get(Number(t.terrain_type));
      if (terrain) return terrain.walkable !== false;
      return !blockedTerrainIds.has(Number(t.terrain_type));
    });
    return candidate?.coordinate ?? 0;
  }, [tiles, terrainById, blockedTerrainIds]);

  const terrainByCoordinate = useMemo(() => {
    const map = new Map();
    tiles
      .filter(t => Number(t.layer) === 0)
      .forEach(t => map.set(Number(t.coordinate), Number(t.terrain_type ?? 0)));
    return map;
  }, [tiles]);

  const [botCoordinate, setBotCoordinate] = useState(0);
  const [messages, setMessages] = useState([]);
  const [cityPrompt, setCityPrompt] = useState(null);

  const eventByCoordinate = useMemo(() => {
    const map = new Map();
    const missionEventsMap = new Map(
      (missionEvents || []).map(ev => [Number(ev.id), ev])
    );
    tiles
      .filter(t => Number(t.layer) === 2 && Number(t.id_event) > 0)
      .forEach(t => {
        const eventId = Number(t.id_event);
        map.set(Number(t.coordinate), {
          id_event: eventId,
          missionEvent: missionEventsMap.get(eventId) || null
        });
      });
    return map;
  }, [tiles, missionEvents]);

  useEffect(() => {
    if (isOpen) {
      setBotCoordinate(firstWalkableCoordinate);
      setMessages(['Prueba iniciada. Usa WASD o flechas para mover el bot.']);
      setCityPrompt(null);
    }
  }, [isOpen, firstWalkableCoordinate]);

  const pushMessage = (text) => {
    setMessages(prev => [text, ...prev].slice(0, 8));
  };

  const canStandAt = (coordinate) => {
    if (coordinate < 0 || coordinate >= width * height) return false;
    const terrainType = terrainByCoordinate.has(Number(coordinate))
      ? terrainByCoordinate.get(Number(coordinate))
      : 0;
    const terrain = terrainById.get(Number(terrainType));
    if (terrain) return terrain.walkable !== false;
    return !blockedTerrainIds.has(Number(terrainType));
  };

  const resolveTileEvents = (coordinate) => {
    const city = cities.find(c => Number(c.coordinate) === Number(coordinate));
    if (city) {
      pushMessage(`Ciudad: ${city.name || `ID ${city.id}`}`);
      setCityPrompt(city);
      return;
    }

    const obj = objects.find(o => Number(o.coordinate) === Number(coordinate));
    if (obj) {
      pushMessage(`Objeto detectado: ${obj.name || `ID ${obj.id_object}`}`);
    }

    const enemy = enemies.find(e => Number(e.coordinate) === Number(coordinate));
    if (enemy) {
      pushMessage(`Encuentro enemigo: ${enemy.name || `ID ${enemy.id_enemy_map}`} (Nv.${enemy.level || '?'})`);
    }

    const eventData = eventByCoordinate.get(Number(coordinate));
    if (eventData) {
      const ev = eventData.missionEvent;
      if (ev) {
        pushMessage(`Evento mision #${ev.id}: ${ev.event_text || 'Sin texto'}`);
        if (Number(ev.reward_money_give) > 0) pushMessage(`+${ev.reward_money_give} dinero`);
        if (Number(ev.reward_item_give) > 0) pushMessage(`Objeto recompensa: ID ${ev.reward_item_give} x${ev.reward_quantity || 1}`);
        let meta = ev.metadata;
        if (typeof meta === 'string') {
          try { meta = JSON.parse(meta); } catch (_) { meta = null; }
        }
        if (meta?.event_enemy_id) pushMessage(`Accion del evento: aparece enemigo ID ${meta.event_enemy_id}`);
        if (meta?.event_teleport_sector || meta?.event_teleport_coordinate !== undefined) {
          const targetSector = Number(meta.event_teleport_sector || 0);
          const targetCoordinate = Number(meta.event_teleport_coordinate || 0);
          if (targetSector === Number(sector?.id) && canStandAt(targetCoordinate)) {
            setBotCoordinate(targetCoordinate);
            pushMessage(`Accion del evento: teletransporte a coord ${targetCoordinate}`);
          } else {
            pushMessage(`Accion del evento: teletransporte a sector ${targetSector || '?'} coord ${targetCoordinate}`);
          }
        }
      } else {
        pushMessage(`Evento mision detectado (ID ${eventData.id_event}).`);
      }
    }

    const portal = portals.find(p => Number(p.coordenada_origen) === Number(coordinate));
    if (portal) {
      const destSector = Number(portal.id_sector_destino);
      const destCoord = Number(portal.coordenada_destino);
      if (destSector === Number(sector?.id)) {
        if (canStandAt(destCoord)) {
          setBotCoordinate(destCoord);
          pushMessage(`Portal interno: ${coordinate} -> ${destCoord}`);
        } else {
          pushMessage(`Portal bloqueado: destino ${destCoord} no transitable.`);
        }
      } else {
        pushMessage(`Portal a sector ${destSector}, coord ${destCoord} (prueba local solo en este sector).`);
      }
    }
  };

  const moveBot = (dx, dy) => {
    if (cityPrompt) return;
    const x = botCoordinate % width;
    const y = Math.floor(botCoordinate / width);
    const nx = x + dx;
    const ny = y + dy;
    if (nx < 0 || nx >= width || ny < 0 || ny >= height) return;
    const next = ny * width + nx;
    if (!canStandAt(next)) {
      const terrainType = terrainByCoordinate.has(Number(next)) ? terrainByCoordinate.get(Number(next)) : 0;
      pushMessage(`Casilla ${next} bloqueada (terreno ${terrainType}).`);
      return;
    }
    setBotCoordinate(next);
    resolveTileEvents(next);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handler = (e) => {
      if (cityPrompt) return;
      const key = e.key.toLowerCase();
      if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'w', 'a', 's', 'd'].includes(key)) {
        e.preventDefault();
      }
      if (key === 'arrowup' || key === 'w') moveBot(0, -1);
      if (key === 'arrowdown' || key === 's') moveBot(0, 1);
      if (key === 'arrowleft' || key === 'a') moveBot(-1, 0);
      if (key === 'arrowright' || key === 'd') moveBot(1, 0);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, botCoordinate, width, height, tiles, portals, enemies, objects, cities, cityPrompt]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm p-4">
      <div className="h-full w-full bg-[#11132d] border border-slate-700 rounded-3xl flex flex-col overflow-hidden">
        <header className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Probar Mapa</h2>
            <p className="text-xs text-slate-400">Sector: {sector?.name} | Coord bot: {botCoordinate}</p>
          </div>
          <button onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl">
            Salir Prueba
          </button>
        </header>

        <div className="flex-1 p-4 grid grid-cols-[1fr_320px] gap-4 min-h-0">
          <MapCanvas
            sector={sector}
            tiles={tiles}
            tilesetConfig={tilesetConfig}
            cities={cities}
            enemies={enemies}
            objects={objects}
            events={Array.from(eventByCoordinate.entries()).map(([coordinate, value]) => ({
              coordinate,
              id_event: value.id_event
            }))}
            portals={portals}
            editorMode="test"
            isDrawing={false}
            onClickTile={(coord) => {
              if (cityPrompt) return;
              if (canStandAt(coord)) {
                setBotCoordinate(coord);
                resolveTileEvents(coord);
              } else {
                pushMessage(`Casilla ${coord} bloqueada.`);
              }
            }}
            testBotCoordinate={botCoordinate}
          />

          <aside className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4 overflow-y-auto custom-scrollbar">
            <h3 className="text-sm font-bold text-slate-300 mb-3">Controles</h3>
            <p className="text-xs text-slate-400 mb-3">Mover: WASD o flechas</p>
            <p className="text-xs text-slate-400 mb-5">Click en casilla para teletransporte de prueba</p>
            <h3 className="text-sm font-bold text-slate-300 mb-3">Eventos</h3>
            <div className="space-y-2">
              {messages.map((msg, idx) => (
                <div key={`${idx}-${msg}`} className="text-xs text-cyan-200 bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-2">
                  {msg}
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>

      {cityPrompt && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl border border-emerald-500/30 bg-[#08111f] shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-emerald-500/20">
              <p className="text-xs uppercase tracking-[0.25em] text-emerald-300/70">Ciudad detectada</p>
              <h3 className="text-xl font-bold text-white mt-1">{cityPrompt.name || 'Asentamiento'}</h3>
              <p className="text-sm text-slate-300 mt-2">
                Tipo: {CITY_TYPES[Number(cityPrompt.city_type ?? 0)] || 'Campamento'} | Clan: {cityPrompt.clan_name || (Number(cityPrompt.clan) === 0 ? 'Neutral' : `Clan #${cityPrompt.clan ?? '-'}`)}
              </p>
            </div>
            <div className="p-5 text-sm text-slate-300 space-y-2">
              <p>El bot ha pisado una ciudad. En el juego real aquí se abrirá la confirmación de acceso.</p>
              <p>Este probador deja el aviso para validar el flujo visual del mapa.</p>
            </div>
            <div className="px-5 pb-5 flex justify-end">
              <button
                type="button"
                onClick={() => setCityPrompt(null)}
                className="rounded-xl border border-emerald-500 bg-emerald-600 px-4 py-2 text-white font-semibold hover:bg-emerald-500"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapTestModal;
